// ScheduleBuilder — explicit schedule selection for Commitment / Planned Spend forms.
// OverlapWarning — shown when API returns POSSIBLE_OVERLAP code.
// Both are pure presentational components with no API calls.

import { useState, useCallback, useMemo } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import type { ForecastInputRecord } from "@workspace/api-client-react";
import type { ScheduleEntry, ScheduleType } from "./types";
import { formatPeriod, formatAmount } from "./helpers";

// ── ScheduleBuilder ───────────────────────────────────────────────────────────

interface ScheduleBuilderProps {
  fiscalYear: number;
  throughPeriod: number;
  totalAmount: number;
  scheduleType: ScheduleType;
  onScheduleTypeChange: (t: ScheduleType) => void;
  schedules: ScheduleEntry[];
  onSchedulesChange: (s: ScheduleEntry[]) => void;
}

export function ScheduleBuilder({
  fiscalYear, throughPeriod, totalAmount,
  scheduleType, onScheduleTypeChange,
  schedules, onSchedulesChange,
}: ScheduleBuilderProps) {
  const futurePeriods = useMemo(() => {
    const result: { fy: number; period: number }[] = [];
    for (let p = throughPeriod + 1; p <= 12; p++) result.push({ fy: fiscalYear, period: p });
    for (let p = 1; p <= 3; p++) result.push({ fy: fiscalYear + 1, period: p });
    return result;
  }, [fiscalYear, throughPeriod]);

  const buildEvenMonthly = useCallback((startPeriod: number, endPeriod: number) => {
    if (!startPeriod || !endPeriod || totalAmount === 0) return;
    const count = endPeriod - startPeriod + 1;
    if (count <= 0) return;
    const monthly = totalAmount / count;
    const entries: ScheduleEntry[] = [];
    for (let p = startPeriod; p <= endPeriod; p++) {
      entries.push({ fiscalYear, period: p, amount: monthly, sourceReference: `manual-${fiscalYear}-${p}` });
    }
    onSchedulesChange(entries);
  }, [fiscalYear, totalAmount, onSchedulesChange]);

  const [evenStart, setEvenStart] = useState(String(throughPeriod + 1));
  const [evenEnd, setEvenEnd] = useState("12");
  const [monthlyAmt, setMonthlyAmt] = useState("");
  const [monthlyPeriod, setMonthlyPeriod] = useState(String(throughPeriod + 1));

  return (
    <div className="fi-schedule-builder" data-testid="fi-schedule-builder">
      <div className="fi-field">
        <label htmlFor="fi-schedule-type-select">Schedule</label>
        <select
          id="fi-schedule-type-select"
          value={scheduleType}
          onChange={e => { onScheduleTypeChange(e.target.value as ScheduleType); onSchedulesChange([]); }}
          data-testid="fi-schedule-type"
        >
          <option value="specific_month">Specific Month</option>
          <option value="even_monthly">Even Monthly</option>
          <option value="monthly_amount">Monthly Amount</option>
          <option value="manual_monthly">Manual Monthly</option>
        </select>
      </div>

      {scheduleType === "specific_month" && (
        <div className="fi-field">
          <label htmlFor="fi-schedule-period-select">Period</label>
          <select
            id="fi-schedule-period-select"
            value={schedules[0] ? `${schedules[0].fiscalYear}-${schedules[0].period}` : ""}
            onChange={e => {
              const [fy, p] = e.target.value.split("-").map(Number);
              onSchedulesChange([{ fiscalYear: fy, period: p, amount: totalAmount, sourceReference: `manual-${fy}-${p}` }]);
            }}
            data-testid="fi-schedule-period"
          >
            <option value="">Select period</option>
            {futurePeriods.map(({ fy, period }) => (
              <option key={`${fy}-${period}`} value={`${fy}-${period}`}>{formatPeriod(period, fy)}</option>
            ))}
          </select>
        </div>
      )}

      {scheduleType === "even_monthly" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, alignItems: "end" }}>
          <div className="fi-field">
            <label htmlFor="fi-schedule-even-start">Start Period</label>
            <select
              id="fi-schedule-even-start"
              value={evenStart}
              onChange={e => setEvenStart(e.target.value)}
              data-testid="fi-schedule-start"
            >
              {futurePeriods.filter(f => f.fy === fiscalYear).map(({ period }) => (
                <option key={period} value={String(period)}>{formatPeriod(period, fiscalYear)}</option>
              ))}
            </select>
          </div>
          <div className="fi-field">
            <label htmlFor="fi-schedule-even-end">End Period</label>
            <select
              id="fi-schedule-even-end"
              value={evenEnd}
              onChange={e => setEvenEnd(e.target.value)}
              data-testid="fi-schedule-end"
            >
              {futurePeriods.filter(f => f.fy === fiscalYear).map(({ period }) => (
                <option key={period} value={String(period)}>{formatPeriod(period, fiscalYear)}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="tl-btn"
            style={{ padding: "9px 10px", fontSize: 10 }}
            onClick={() => buildEvenMonthly(Number(evenStart), Number(evenEnd))}
            aria-label="Apply even monthly schedule"
          >
            Apply
          </button>
        </div>
      )}

      {scheduleType === "monthly_amount" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div className="fi-field">
            <label htmlFor="fi-monthly-amount-input">Monthly Amount ($)</label>
            <input
              id="fi-monthly-amount-input"
              type="number"
              min={0}
              value={monthlyAmt}
              onChange={e => setMonthlyAmt(e.target.value)}
              placeholder="0"
              data-testid="fi-monthly-amount"
            />
          </div>
          <div className="fi-field">
            <label htmlFor="fi-monthly-start-select">Starting Period</label>
            <select
              id="fi-monthly-start-select"
              value={monthlyPeriod}
              onChange={e => setMonthlyPeriod(e.target.value)}
              data-testid="fi-monthly-start"
            >
              {futurePeriods.filter(f => f.fy === fiscalYear).map(({ period }) => (
                <option key={period} value={String(period)}>{formatPeriod(period, fiscalYear)}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="tl-btn"
            style={{ gridColumn: "1/-1", fontSize: 10, padding: "8px" }}
            onClick={() => {
              const monthly = Number(monthlyAmt);
              if (!monthly) return;
              const entries: ScheduleEntry[] = futurePeriods
                .filter(f => f.fy === fiscalYear && f.period >= Number(monthlyPeriod))
                .map(({ fy, period }) => ({ fiscalYear: fy, period, amount: monthly, sourceReference: `manual-${fy}-${period}` }));
              onSchedulesChange(entries);
            }}
            aria-label="Apply monthly amount schedule"
          >
            Apply Monthly Amount
          </button>
        </div>
      )}

      {scheduleType === "manual_monthly" && (
        <div className="fi-manual-grid">
          {futurePeriods.filter(f => f.fy === fiscalYear).map(({ fy, period }) => {
            const entry = schedules.find(s => s.fiscalYear === fy && s.period === period);
            const inputId = `fi-manual-period-input-${period}`;
            return (
              <div key={`${fy}-${period}`} className="fi-field fi-manual-field">
                <label htmlFor={inputId}>{formatPeriod(period, fy)}</label>
                <input
                  id={inputId}
                  type="number"
                  min={0}
                  placeholder="0"
                  value={entry?.amount ?? ""}
                  onChange={e => {
                    const val = Number(e.target.value);
                    const next = schedules.filter(s => !(s.fiscalYear === fy && s.period === period));
                    if (val) next.push({ fiscalYear: fy, period, amount: val, sourceReference: `manual-${fy}-${period}` });
                    onSchedulesChange(next);
                  }}
                  aria-label={`Amount for ${formatPeriod(period, fy)}`}
                  data-testid={`fi-manual-period-${period}`}
                />
              </div>
            );
          })}
        </div>
      )}

      {schedules.length > 0 && (
        <div className="fi-schedule-preview" aria-label="Schedule preview">
          <span className="fi-schedule-preview-label">Schedule preview</span>
          <div className="fi-schedule-preview-items">
            {schedules.map((s, i) => (
              <span key={i} className="fi-schedule-item">
                {formatPeriod(s.period, s.fiscalYear)}: {formatAmount(s.amount)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── OverlapWarning ────────────────────────────────────────────────────────────

interface OverlapWarningProps {
  overlaps: ForecastInputRecord[];
  onSaveAnyway: () => void;
  onCancel: () => void;
  isPending: boolean;
}

export function OverlapWarning({ overlaps, onSaveAnyway, onCancel, isPending }: OverlapWarningProps) {
  return (
    <div className="fi-overlap-warning" role="alert" data-testid="fi-overlap-warning">
      <div className="fi-overlap-header">
        <AlertTriangle size={14} aria-hidden="true" />
        <b>Possible Overlap</b>
      </div>
      <p>An active input may already cover this spend. Review existing records before saving.</p>
      <div className="fi-overlap-list">
        {overlaps.map(o => (
          <div key={o.inputId} className="fi-overlap-item">
            <span>{o.description}</span>
            <span className="fi-overlap-meta">
              {o.vendor && <>{o.vendor} · </>}
              {o.schedules.length > 0 ? formatPeriod(o.schedules[0].period, o.schedules[0].fiscalYear) : "—"}
              {" · "}{formatAmount(o.amount)}
            </span>
          </div>
        ))}
      </div>
      <div className="fi-overlap-actions">
        <button
          className="tl-btn"
          onClick={onCancel}
          aria-label="Go back without saving"
          data-testid="fi-overlap-cancel"
        >
          Back
        </button>
        <button
          className="tl-btn primary"
          onClick={onSaveAnyway}
          disabled={isPending}
          aria-label="Save this input despite the overlap"
          data-testid="fi-save-anyway"
        >
          {isPending ? <><Loader2 size={12} className="animate-spin" aria-hidden="true" /> Saving…</> : "Save Anyway"}
        </button>
      </div>
    </div>
  );
}
